import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import { actualizarDatosTemporalRitEntregas } from "../../services/reporteRit/temporalRitEntregasService";

import {
  Button, Typography, message, Card, Table, Tag, Space,
  Divider, Input, Select, Row, Col, Progress, Spin, Tooltip, Modal,Empty
} from "antd";
import {
  SearchOutlined, ClearOutlined,
  FileExcelOutlined, UserOutlined, FilterOutlined,
  LeftOutlined, RightOutlined, DownloadOutlined,
  SyncOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import {
  obtenerEntregasPaginado,
  obtenerTotalEntregas,
  obtenerTodosEntregasEnBloques,
  obtenerPorMunicipio,
  obtenerPorUsuario,
  obtenerPorNroEntrega,
  obtenerPorDocumento,
  obtenerMunicipios,
  obtenerUsuarios,
} from "../../services/reporteRit/promotorEncuestadoEntregasService";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/Logo_Prosperidad_Social.png";
import { esSupervisorDps } from "../../utils/roles";

const { Title, Text } = Typography;
const { Option } = Select;

const PAGE_SIZE_OPTIONS = [100, 300, 500];
const DEFAULT_PAGE_SIZE = 100;

const MENSAJES_PROGRESO = [
  "Conectando con el servidor...",
  "Obteniendo registros del API RIT...",
  "Procesando información recibida...",
  "Limpiando datos anteriores...",
  "Insertando registros en lotes...",
  "Validando integridad de los datos...",
  "Guardando cambios en la base de datos...",
  "Finalizando sincronización...",
];

const MENSAJES_CARGA = [
  "Obteniendo registros de entregas...",
  "Cargando catálogo de municipios...",
  "Cargando lista de encuestadores...",
  "Preparando los filtros...",
  "Casi listo...",
];

const nombreEncuestador = (row) =>
  [row.usuario_nombres, row.usuario_apellidos].filter(Boolean).join(" ") || "—";

const ReporteEntregas = () => {
  const usernameFromState = useSelector((state) => state.auth.user);
  const appName           = "reporteRIT";
  const permissions       = useSelector((state) => selectAppPermissions(state, appName));
  const permissionUpdate  = permissions.priv_update === "Y";
  const esAdmin           = usernameFromState?.role === "1";
  // Supervisor DPS: misma vista nacional (solo lectura) que el admin. La
  // sincronización ("Actualizar datos") sigue dependiendo de priv_update.
  const vistaNacional     = esAdmin || esSupervisorDps(usernameFromState);
  const esCoordinador     = usernameFromState?.role === "2";

  const [loadingInicial,   setLoadingInicial]   = useState(true);
  const [loadingConsultar, setLoadingConsultar] = useState(false);
  const [mensajeCarga,     setMensajeCarga]     = useState(MENSAJES_CARGA[0]);

  const [rawData,        setRawData]        = useState([]);
  const [rawDataResumen, setRawDataResumen] = useState([]);

  // ── Paginación por cursor ─────────────────────────────────────────────────
  const [cursors,        setCursors]        = useState([0]);
  const [cursorIndex,    setCursorIndex]    = useState(0);
  const [hasMore,        setHasMore]        = useState(false);
  const [nextCursor,     setNextCursor]     = useState(null);
  const [pageSize,       setPageSize]       = useState(DEFAULT_PAGE_SIZE);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const [modoFiltro,  setModoFiltro]  = useState("paginado");
  const [modoResumen, setModoResumen] = useState(false);

  // ── Progreso resumen ──────────────────────────────────────────────────────
  const [loadingResumen,  setLoadingResumen]  = useState(false);
  const [progresoResumen, setProgresoResumen] = useState(0);
  const [filasResumen,    setFilasResumen]    = useState(0);

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const [municipios,           setMunicipios]           = useState([]);
  const [encuestadores,        setEncuestadores]        = useState([]);
  const [loadingMunicipios,    setLoadingMunicipios]    = useState(false);
  const [loadingEncuestadores, setLoadingEncuestadores] = useState(false);
  const [municipioAdmin,       setMunicipioAdmin]       = useState(null);

  // ── Filtros de tabla ──────────────────────────────────────────────────────
  const [filtroDocumento,   setFiltroDocumento]   = useState("");
  const [filtroEncuestador, setFiltroEncuestador] = useState(null);
  const [filtroNroEntrega,  setFiltroNroEntrega]  = useState(null);
  const [filtroEstado,      setFiltroEstado]      = useState(null);

  // ── Descarga Excel ────────────────────────────────────────────────────────
  const [descargando,      setDescargando]      = useState(false);
  const [progresoDescarga, setProgresoDescarga] = useState(0);
  const [filasDescargadas, setFilasDescargadas] = useState(0);

  // ── Modal sincronización ──────────────────────────────────────────────────
  const [modalVisible,             setModalVisible]             = useState(false);
  const [progreso,                 setProgreso]                 = useState(0);
  const [mensajeActual,            setMensajeActual]            = useState(MENSAJES_PROGRESO[0]);
  const [sincronizacionFinalizada, setSincronizacionFinalizada] = useState(false);
  const [totalSincronizado,        setTotalSincronizado]        = useState(0);

  const intervalRef     = useRef(null);
  const mensajeIdxRef   = useRef(0);
  const mensajeCargaRef = useRef(null);

  const entregasDisponibles = [...new Set(rawData.map((r) => r.nroEntrega).filter(Boolean))];

  

  // ── Carga inicial — bifurcada por rol ─────────────────────────────────────
  useEffect(() => {
    let idx = 0;
    mensajeCargaRef.current = setInterval(() => {
      idx = (idx + 1) % MENSAJES_CARGA.length;
      setMensajeCarga(MENSAJES_CARGA[idx]);
    }, 1800);

    const init = async () => {
      setLoadingInicial(true);
      try {
        await cargarCatalogos(esCoordinador, usernameFromState?.cod_municipio);

        if (vistaNacional) {
          // Admin: paginado completo + total
          await Promise.all([
            cargarPaginado(0, DEFAULT_PAGE_SIZE, true, true),
            obtenerTotalEntregas().then(setTotalRegistros),
          ]);
        } else {
          // Coordinador: solo su municipio
          await cargarTablaMunicipio(true);
        }
      } catch (e) {
        console.error(e);
      } finally {
        clearInterval(mensajeCargaRef.current);
        setLoadingInicial(false);
      }
    };
    init();

    return () => clearInterval(mensajeCargaRef.current);
  }, []);

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const cargarCatalogos = async (coordinador = false, codMunicipio = null) => {
    setLoadingMunicipios(true);
    setLoadingEncuestadores(true);
    try {
      const [muni, usrs] = await Promise.all([
        obtenerMunicipios(),
        obtenerUsuarios(coordinador && codMunicipio ? Number(codMunicipio) : null),
      ]);
      if (muni?.success) setMunicipios(muni.data || []);
      if (usrs?.success) {
        setEncuestadores(
          (usrs.data || []).map((u) => ({
            value: u.usuario_oid,
            label: `${u.usuario_nombres} ${u.usuario_apellidos}`.trim(),
          }))
        );
      }
    } catch {
      message.error("Error al cargar los catálogos de filtros");
    } finally {
      setLoadingMunicipios(false);
      setLoadingEncuestadores(false);
    }
  };

  // ── NUEVO: carga por municipio del coordinador ────────────────────────────
  const cargarTablaMunicipio = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      if (!usernameFromState?.cod_municipio) {
        // Antes esto fallaba en silencio (obtenerPorMunicipio(undefined) -> error del
        // backend) y, como esta función siempre corre con silencioso=true incluso al
        // dar clic en "Actualizar datos", el usuario nunca se enteraba: el modal decía
        // "sincronización completada" pero la tabla se quedaba vacía sin explicación.
        message.error("Tu usuario no tiene un municipio asignado. Contacta al administrador para poder ver tus entregas.");
        return;
      }

      const resultado = await obtenerPorMunicipio(usernameFromState.cod_municipio);
      if (!resultado?.success) return;

      setRawData(mapearFilas(resultado.data || []));
      setModoFiltro("municipio");
      setHasMore(false);
      limpiarFiltrosSinRecargar();
      if (!silencioso)
        resultado.data.length > 0
          ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros cargados`)
          : message.warning("No se encontraron registros en la base de datos");
    } catch (error) {
      console.error(error);
      message.error("Error al consultar los datos de entregas");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [usernameFromState]);

  // ── Mapeo de filas ────────────────────────────────────────────────────────
  const mapearFilas = (items) =>
  items.map((item) => ({
    key:                   item.id,
    id:                    item.id,
    idencuesta:            item.idencuesta,
    documentoTitular:      item.numero_documento_titular ?? "—",
    nombreCompleto:        item.nombre_titular            ?? "—",
    nombreProyecto:        item.nombre_proyecto          ?? "—",
    nroEntrega:            item.nro_entrega,
    puntoEntrega:          item.punto_entrega            ?? "—",
    quienReclama:          item.quien_reclama_beneficio  ?? "—",
    documentoAutorizado:   item.numero_documento_aut      ?? "—",
    nombreAutorizado:      item.nombre_completo_aut       ?? "—",
    encuestadorId:         item.usuario_oid,
    encuestador:           nombreEncuestador(item),
    municipioId:           item.municipio_id,
    municipio:             item.municipio                ?? "—",
    departamentoId:        item.departamento_id,
    departamento:          item.departamento             ?? "—",
    estado:                item.estado,
    fechaDiligenciamiento: item.fecha_diligenciamiento   ?? "—",
    horaFinaliza:          item.horafinaliza             ?? "—",
  }));

  const mapearFilasExport = (items) =>
  items.map((item) => ({
    "ID":                     item.id,
    "ID Encuesta":            item.idencuesta,
    "Quién Reclama":          item.quien_reclama_beneficio  ?? "—",
    "Documento":              item.numero_documento_titular ?? "—",
    "Nombre Completo Titular": item.nombre_titular           ?? "—",
    "Documento Autorizado":   item.numero_documento_aut      ?? "—",
    "Nombre Autorizado":      item.nombre_completo_aut       ?? "—",
    "Proyecto":               item.nombre_proyecto          ?? "—",
    "N° Entrega":             item.nro_entrega              ?? "—",
    "Punto Entrega":          item.punto_entrega            ?? "—",
    "Departamento":           item.departamento             ?? "—",
    "Municipio":              item.municipio                ?? "—",
    "Encuestador":            nombreEncuestador(item),
    "Estado":                 item.estado === 1 ? "Activo" : "Inactivo",
    "Fecha Diligenciamiento": item.fecha_diligenciamiento   ?? "—",
    "Hora Finaliza":          item.horafinaliza             ?? "—",
  }));

  // ── Paginación ────────────────────────────────────────────────────────────
  const cargarPaginado = useCallback(async (
    lastId     = 0,
    size       = DEFAULT_PAGE_SIZE,
    reiniciar  = false,
    silencioso = false,
  ) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      const resultado = await obtenerEntregasPaginado(lastId, size);
      setRawData(mapearFilas(resultado.data || []));
      setHasMore(resultado.hasMore);
      setNextCursor(resultado.nextCursor);
      setModoFiltro("paginado");
      limpiarFiltrosSinRecargar();
      if (reiniciar) { setCursors([0]); setCursorIndex(0); }
      if (!silencioso)
        (resultado.data || []).length > 0
          ? message.success(`${resultado.data.length} registros cargados`)
          : message.warning("No se encontraron registros");
    } catch {
      if (!silencioso) message.error("Error al consultar las entregas");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, []);

  // ── Resumen completo ──────────────────────────────────────────────────────
  const cargarResumenCompleto = async () => {
    setLoadingResumen(true);
    setProgresoResumen(0);
    setFilasResumen(0);
    try {
      let dataCompleta = [];

      if (vistaNacional && modoFiltro === "paginado") {
        // Admin sin filtro: descarga todos los registros en bloques
        const todos = await obtenerTodosEntregasEnBloques(
          (pct, cargadas) => {
            setProgresoResumen(pct ?? 0);
            setFilasResumen(cargadas);
          },
          totalRegistros,
          500,
        );
        dataCompleta = mapearFilas(todos);
      } else if (modoFiltro === "municipio") {
        // Coordinador (o admin con municipio activo): usa los datos ya cargados
        dataCompleta = rawData;
      } else {
        // Cualquier otro filtro activo: usa los datos ya en pantalla
        dataCompleta = rawData;
      }

      setRawDataResumen(dataCompleta);
    } catch (error) {
      console.error(error);
      message.error("Error cargando resumen");
    } finally {
      setLoadingResumen(false);
      setProgresoResumen(0);
      setFilasResumen(0);
    }
  };

  const irSiguientePagina = () => {
    if (!hasMore || nextCursor === null) return;
    const nuevosCursors = [...cursors.slice(0, cursorIndex + 1), nextCursor];
    setCursors(nuevosCursors);
    const nuevoIndex = cursorIndex + 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(nextCursor, pageSize);
  };

  const irPaginaAnterior = () => {
    if (cursorIndex === 0) return;
    const nuevoIndex = cursorIndex - 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(cursors[nuevoIndex], pageSize);
  };

  const handlePageSizeChange = (nuevoSize) => {
    setPageSize(nuevoSize);
    cargarPaginado(0, nuevoSize, true);
  };

  // ── Handler municipio admin ───────────────────────────────────────────────
  const handleMunicipioAdminChange = async (value) => {
    setMunicipioAdmin(value ?? null);
    if (!value) { await cargarPaginado(0, pageSize, true); return; }
    setLoadingConsultar(true);
    try {
      const resultado = await obtenerPorMunicipio(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data || []));
      setModoFiltro("municipio");
      setHasMore(false);
      limpiarFiltrosSinRecargar();
      message.success(`${(resultado.data || []).length.toLocaleString("es-CO")} registros del municipio`);
    } catch {
      message.error("Error al filtrar por municipio");
    } finally {
      setLoadingConsultar(false);
    }
  };

  // ── Handlers filtros secundarios ──────────────────────────────────────────
  // El input solo actualiza el estado local (dataFiltrada filtra por subcadena
  // sobre lo ya cargado). Cuando el modo activo es "paginado" eso no alcanza:
  // rawData solo tiene la página actual, así que un documento fuera de esa
  // página nunca aparece, se pegue o se escriba letra por letra. El efecto de
  // más abajo complementa esto consultando al backend por coincidencia exacta.
  const handleDocumentoChange = (e) => {
    setFiltroDocumento(e.target.value.replace(/\D/g, ""));
  };

  // Búsqueda exacta por documento contra el backend. Solo aplica en modo
  // "paginado"/"documento": en municipio/encuestador/nroEntrega ya se cargó
  // el conjunto completo de esa vista y el filtro local basta.
  const DOCUMENTO_MIN_LENGTH = 4;
  const documentoDebounceRef = useRef(null);

  useEffect(() => {
    if (modoFiltro !== "paginado" && modoFiltro !== "documento") return;

    clearTimeout(documentoDebounceRef.current);

    if (!filtroDocumento) {
      if (modoFiltro === "documento") cargarPaginado(0, pageSize, true);
      return;
    }

    if (filtroDocumento.length < DOCUMENTO_MIN_LENGTH) return;

    documentoDebounceRef.current = setTimeout(async () => {
      setLoadingConsultar(true);
      try {
        const resultado = await obtenerPorDocumento(filtroDocumento);
        if (!resultado?.success) return;
        const data = resultado.data || [];
        setRawData(mapearFilas(data));
        setModoFiltro("documento");
        setHasMore(false);
        data.length > 0
          ? message.success(`${data.length.toLocaleString("es-CO")} registro(s) encontrado(s)`)
          : message.warning("No se encontraron registros con ese documento");
      } catch (error) {
        if (error?.response?.status === 404) {
          setRawData([]);
          setModoFiltro("documento");
          setHasMore(false);
          message.warning("No se encontraron registros con ese documento");
        } else {
          console.error(error);
          message.error("Error al buscar por número de documento");
        }
      } finally {
        setLoadingConsultar(false);
      }
    }, 450);

    return () => clearTimeout(documentoDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroDocumento]);

  const handleEncuestadorChange = useCallback(async (value) => {
    if (!value) {
      setFiltroEncuestador(null);
      if (modoFiltro === "encuestador") {
        vistaNacional ? await cargarPaginado(0, pageSize, true) : await cargarTablaMunicipio(true);
      }
      return;
    }
    // Coordinador: filtro local sobre datos ya cargados
    if (esCoordinador) {
      setFiltroEncuestador(value);
      return;
    }
    if (modoFiltro === "municipio") return;
    setFiltroEncuestador(value);
    setLoadingConsultar(true);
    try {
      const resultado = await obtenerPorUsuario(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data || []));
      setModoFiltro("encuestador");
      setHasMore(false);
      resultado.data.length > 0
        ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del encuestador`)
        : message.warning("No se encontraron registros para ese encuestador");
    } catch {
      message.error("Error al filtrar por encuestador");
    } finally {
      setLoadingConsultar(false);
    }
  }, [modoFiltro, pageSize, cargarPaginado, cargarTablaMunicipio, esCoordinador, vistaNacional]);

  const handleNroEntregaChange = useCallback(async (value) => {
    setFiltroNroEntrega(value ?? null);
    if (value == null) {
      if (modoFiltro === "nroEntrega") {
        vistaNacional ? await cargarPaginado(0, pageSize, true) : await cargarTablaMunicipio(true);
      }
      return;
    }
    // Coordinador: filtro local
    if (esCoordinador) return;
    if (modoFiltro === "municipio") return;
    setLoadingConsultar(true);
    try {
      const resultado = await obtenerPorNroEntrega(value);
      if (!resultado) { message.error("Error al consultar el servicio"); return; }
      const data = resultado.data || [];
      setRawData(mapearFilas(data));
      setModoFiltro("nroEntrega");
      setHasMore(false);
      data.length === 0
        ? message.warning(`No hay registros para la entrega ${value}`)
        : message.success(`${data.length.toLocaleString("es-CO")} registros de la entrega ${value}`);
    } catch {
      message.error("Error al filtrar por número de entrega");
    } finally {
      setLoadingConsultar(false);
    }
  }, [modoFiltro, pageSize, cargarPaginado, cargarTablaMunicipio, esCoordinador, vistaNacional]);

  const handleEstadoChange = (value) => setFiltroEstado(value ?? null);

  // ── dataFiltrada ──────────────────────────────────────────────────────────
  const dataFiltrada = useMemo(() => {
    return rawData.filter((row) => {
      const coincideDocumento =
        !filtroDocumento ||
        String(row.documentoTitular).toLowerCase().includes(filtroDocumento.toLowerCase());

      const coincideEncuestador = (() => {
        if (!filtroEncuestador) return true;
        const rowKey = row.encuestadorId != null
          ? String(row.encuestadorId)
          : `__n__${row.encuestador}`;
        return rowKey === String(filtroEncuestador);
      })();

      const coincideNroEntrega = filtroNroEntrega == null || row.nroEntrega === filtroNroEntrega;
      const coincideEstado     = filtroEstado == null || row.estado === filtroEstado;

      return coincideDocumento && coincideEncuestador && coincideNroEntrega && coincideEstado;
    });
  }, [rawData, filtroDocumento, filtroEncuestador, filtroNroEntrega, filtroEstado]);

  // ── dataFiltradaResumen ───────────────────────────────────────────────────
  const dataFiltradaResumen = useMemo(() => {
    return rawDataResumen.filter((row) => {
      const coincideDocumento   =
        !filtroDocumento ||
        String(row.documentoTitular).toLowerCase().includes(filtroDocumento.toLowerCase());
      const coincideEncuestador =
        !filtroEncuestador ||
        String(row.encuestadorId) === String(filtroEncuestador);
      const coincideNroEntrega  = filtroNroEntrega == null || row.nroEntrega === filtroNroEntrega;
      const coincideEstado      = filtroEstado == null || row.estado === filtroEstado;
      return coincideDocumento && coincideEncuestador && coincideNroEntrega && coincideEstado;
    });
  }, [rawDataResumen, filtroDocumento, filtroEncuestador, filtroNroEntrega, filtroEstado]);

  // ── resumenData ───────────────────────────────────────────────────────────
  const resumenData = useMemo(() => {
    const mapa = {};
    dataFiltradaResumen.forEach((row) => {
      const key = row.municipioId ?? "sin_municipio";
      if (!mapa[key]) {
        mapa[key] = {
          key,
          municipio:     row.municipio,
          departamento:  row.departamento,
          totalEntregas: 0,
        };
      }
      mapa[key].totalEntregas += 1;
    });
    return Object.values(mapa).sort((a, b) => a.municipio.localeCompare(b.municipio));
  }, [dataFiltradaResumen]);

  const hayFiltrosActivos =
    !!filtroDocumento || !!filtroEncuestador ||
    filtroNroEntrega !== null || filtroEstado !== null;

  // ── Limpiar filtros ───────────────────────────────────────────────────────
  const limpiarFiltrosSinRecargar = () => {
    setFiltroDocumento("");
    setFiltroEncuestador(null);
    setFiltroNroEntrega(null);
    setFiltroEstado(null);
  };

  const limpiarFiltros = () => {
    limpiarFiltrosSinRecargar();
    if (esCoordinador) {
      // Coordinador: los filtros son locales, no hay recarga de API necesaria
      return;
    }
    if (
      modoFiltro === "paginado"    ||
      modoFiltro === "encuestador" ||
      modoFiltro === "documento"   ||
      modoFiltro === "nroEntrega"
    ) {
      cargarPaginado(0, pageSize, true);
    }
  };

  // ── Sincronización ────────────────────────────────────────────────────────
  const iniciarProgreso = () => {
    setProgreso(0);
    setSincronizacionFinalizada(false);
    setMensajeActual(MENSAJES_PROGRESO[0]);
    mensajeIdxRef.current = 0;
    setModalVisible(true);
    intervalRef.current = setInterval(() => {
      setProgreso((prev) => {
        if (prev >= 85) { clearInterval(intervalRef.current); return prev; }
        const siguiente = prev + Math.random() * 2 + 0.5;
        const nuevoIdx  = Math.min(Math.floor(siguiente / 12), MENSAJES_PROGRESO.length - 1);
        if (nuevoIdx !== mensajeIdxRef.current) {
          mensajeIdxRef.current = nuevoIdx;
          setMensajeActual(MENSAJES_PROGRESO[nuevoIdx]);
        }
        return Math.min(siguiente, 85);
      });
    }, 600);
  };

  const finalizarProgreso = useCallback((total) => {
    return new Promise((resolve) => {
      clearInterval(intervalRef.current);
      setMensajeActual("¡Sincronización completada!");
      setTotalSincronizado(total);
      let p = 85;
      const finish = setInterval(() => {
        p += 3;
        setProgreso(Math.min(p, 100));
        if (p >= 100) {
          clearInterval(finish);
          setSincronizacionFinalizada(true);
          resolve();
        }
      }, 60);
    });
  }, []);

  const encuestadoresEfectivos = useMemo(() => {
    if (!esCoordinador || rawData.length === 0) return encuestadores;

    const mapa = new Map();
    rawData.forEach((row) => {
      const nombre = row.encuestador;
      if (!nombre || nombre === "—") return;

      // Si tiene ID lo usamos; si no, el nombre mismo es la clave
      const key = row.encuestadorId != null
        ? String(row.encuestadorId)
        : `__n__${nombre}`;

      if (!mapa.has(key)) mapa.set(key, nombre);
    });

    return [...mapa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rawData, encuestadores, esCoordinador]);
  
  // ── NUEVO: handleActualizar bifurcado por rol ─────────────────────────────
  const handleActualizar = async () => {
    iniciarProgreso();
    try {
      const resp = await actualizarDatosTemporalRitEntregas();
      await finalizarProgreso(resp.total_registros ?? resp.total ?? 0);
      cargarCatalogos(esCoordinador, usernameFromState?.cod_municipio);

      if (vistaNacional) {
        obtenerTotalEntregas().then(setTotalRegistros);
        if (municipioAdmin) {
          const resultado = await obtenerPorMunicipio(municipioAdmin);
          if (resultado?.success) setRawData(mapearFilas(resultado.data || []));
        } else {
          await cargarPaginado(0, pageSize, true, true);
        }
      } else {
        // Coordinador: refresca solo su municipio
        await cargarTablaMunicipio(true);
      }
    } catch (error) {
      console.error(error);
      clearInterval(intervalRef.current);
      setModalVisible(false);
      message.error("Error al sincronizar los datos de entregas");
    }
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setProgreso(0);
    setSincronizacionFinalizada(false);
  };

  // ── Excel ─────────────────────────────────────────────────────────────────
  const aplicarEncabezadoSheet = async (workbook, worksheet, titulo, colFin) => {
    try {
      const response1 = await fetch(logo);
      const buffer1   = await response1.arrayBuffer();
      const imageId1  = workbook.addImage({ buffer: buffer1, extension: "png" });
      worksheet.mergeCells(`A1:B3`);
      worksheet.addImage(imageId1, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    } catch {
      console.log("Logo no cargado");
    }
    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell     = worksheet.getCell("C1");
    titleCell.value     = titulo;
    titleCell.font      = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell     = worksheet.getCell("C3");
    dateCell.value     = `Fecha de exportación: ${new Date().toLocaleDateString()}`;
    dateCell.alignment = { horizontal: "center" };

    worksheet.addRow([]);
  };

  const agregarTablaSheet = (worksheet, filas, headerColor = "FF217346") => {
    const headers   = Object.keys(filas[0]);
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: headerColor } };
      cell.alignment = { horizontal: "center" };
      cell.border    = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
    });
    filas.forEach((fila) => {
      const row = worksheet.addRow(Object.values(fila));
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
      });
    });
    worksheet.columns = headers.map((header) => ({
      width: Math.max(header.length, ...filas.map((row) => String(row[header] ?? "").length)) + 3,
    }));
  };

  const calcularResumenParaExport = (filas) => {
    const mapa = {};
    filas.forEach((row) => {
      const key = row["Municipio"] || "Sin municipio";
      if (!mapa[key]) {
        mapa[key] = {
          "Departamento":   row["Departamento"] ?? "—",
          "Municipio":      row["Municipio"]    ?? "—",
          "Total Entregas": 0,
        };
      }
      mapa[key]["Total Entregas"] += 1;
    });
    return Object.values(mapa).sort((a, b) =>
      String(a["Municipio"]).localeCompare(String(b["Municipio"]))
    );
  };

  const generarExcel = async (filasDetalle, filasResumenParam = []) => {
    if (!filasDetalle.length) { message.warning("No hay datos para exportar"); return; }

    const workbook = new ExcelJS.Workbook();

    const wsDetalle = workbook.addWorksheet("Entregas RIT");
    await aplicarEncabezadoSheet(workbook, wsDetalle, "REPORTE RIT - ENTREGAS", "N");
    agregarTablaSheet(wsDetalle, filasDetalle, "FF217346");

    const resumenFinal = filasResumenParam.length
      ? filasResumenParam
      : calcularResumenParaExport(filasDetalle);

    if (resumenFinal.length) {
      const wsResumen = workbook.addWorksheet("Resumen por Municipio");
      await aplicarEncabezadoSheet(workbook, wsResumen, "RESUMEN RIT - ENTREGAS POR MUNICIPIO", "E");
      agregarTablaSheet(wsResumen, resumenFinal, "FF1565C0");

      const totalGeneral = resumenFinal.reduce((s, r) => s + r["Total Entregas"], 0);
      const totalRow = wsResumen.addRow(["", "TOTAL GENERAL", totalGeneral]);
      totalRow.eachCell((cell) => {
        cell.font   = { bold: true };
        cell.border = { top: { style: "thin" }, bottom: { style: "double" }, left: { style: "thin" }, right: { style: "thin" } };
        cell.fill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Entregas_RIT_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const descargarExcel = async () => {
    // Coordinador o admin con filtro activo: exporta lo que tiene en pantalla
    const exportarParcial = !vistaNacional || modoFiltro !== "paginado";
    if (exportarParcial) {
      if (!dataFiltrada.length) { message.warning("No hay datos para exportar"); return; }
        const filas = dataFiltrada.map((row) => ({
          "ID":                     row.id,
          "ID Encuesta":            row.idencuesta,
          "Quién Reclama":          row.quienReclama,
          "Documento":              row.documentoTitular,
          "Nombre Completo Titular": row.nombreCompleto,
          "Documento Autorizado":   row.documentoAutorizado,
          "Nombre Autorizado":      row.nombreAutorizado,
          "Proyecto":               row.nombreProyecto,
          "N° Entrega":             row.nroEntrega ?? "—",
          "Punto Entrega":          row.puntoEntrega,
          "Departamento":           row.departamento,
          "Municipio":              row.municipio,
          "Encuestador":            row.encuestador,
          "Estado":                 row.estado === 1 ? "Activo" : "Inactivo",
          "Fecha Diligenciamiento": row.fechaDiligenciamiento,
          "Hora Finaliza":          row.horaFinaliza,
        }));
      await generarExcel(filas);
      message.success(`Excel exportado con ${filas.length.toLocaleString("es-CO")} registros (2 pestañas)`);
      return;
    }

    setDescargando(true);
    setProgresoDescarga(0);
    setFilasDescargadas(0);
    try {
      const todos = await obtenerTodosEntregasEnBloques(
        (pct, cargadas) => { setProgresoDescarga(pct ?? 0); setFilasDescargadas(cargadas); },
        totalRegistros,
        500,
      );
      const filasMapeadas  = mapearFilasExport(todos);
      const filasFiltradas = hayFiltrosActivos
        ? filasMapeadas.filter((row) => {
            const coincideDocumento =
              !filtroDocumento ||
              String(row["Documento"] ?? "").toLowerCase().includes(filtroDocumento.toLowerCase());
            const coincideEstado =
              filtroEstado == null ||
              (filtroEstado === 1 ? row["Estado"] === "Activo" : row["Estado"] === "Inactivo");
            return coincideDocumento && coincideEstado;
          })
        : filasMapeadas;

      setProgresoDescarga(100);
      message.success(
        hayFiltrosActivos
          ? `Exportando ${filasFiltradas.length.toLocaleString("es-CO")} de ${filasMapeadas.length.toLocaleString("es-CO")} registros (2 pestañas)`
          : `Excel generado con ${filasFiltradas.length.toLocaleString("es-CO")} registros (2 pestañas)`
      );
      await generarExcel(filasFiltradas);
    } catch {
      message.error("Error al descargar los datos para Excel");
    } finally {
      setDescargando(false);
      setProgresoDescarga(0);
      setFilasDescargadas(0);
    }
  };

  // ── Columnas ──────────────────────────────────────────────────────────────
    const columns = [
      { title: "ID",               dataIndex: "id",               key: "id",               width: 80,  align: "center" },
      { title: "ID Encuesta",      dataIndex: "idencuesta",       key: "idencuesta",       width: 120, align: "center" },
      { title: "Quién Reclama", dataIndex: "quienReclama", key: "quienReclama", ellipsis: true, width: 200 },
      { title: "Documento",        dataIndex: "documentoTitular", key: "documentoTitular", width: 150 },
      { title: "Nombre Completo Titular", dataIndex: "nombreCompleto", key: "nombreCompleto", ellipsis: true, width: 220 },
      { title: "Documento Autorizado", dataIndex: "documentoAutorizado", key: "documentoAutorizado", width: 170 },
      { title: "Nombre Autorizado",    dataIndex: "nombreAutorizado",    key: "nombreAutorizado",    ellipsis: true, width: 220 },
      { title: "Proyecto",         dataIndex: "nombreProyecto",   key: "nombreProyecto",   ellipsis: true, width: 200 },
      {
        title: "N° Entrega", dataIndex: "nroEntrega", key: "nroEntrega",
        width: 110, align: "center",
        render: (val) => val != null ? <Tag color="blue">Entrega {val}</Tag> : "—",
      },
      { title: "Departamento",  dataIndex: "departamento", key: "departamento", width: 150 },
      { title: "Municipio",     dataIndex: "municipio",    key: "municipio",    width: 150 },
      { title: "Punto Entrega", dataIndex: "puntoEntrega", key: "puntoEntrega", ellipsis: true, width: 200 },
      {
        title: "Estado", dataIndex: "estado", key: "estado", width: 110, align: "center",
        render: (val) => val === 1
          ? <Tag color="success">Activo</Tag>
          : <Tag color="error">Inactivo</Tag>,
      },
      { title: "Fecha Diligenciamiento", dataIndex: "fechaDiligenciamiento", key: "fechaDiligenciamiento", width: 195 },
      { title: "Hora Finaliza",          dataIndex: "horaFinaliza",          key: "horaFinaliza",          width: 130 },
    ];


  const columnsResumen = [
    { title: "Departamento", dataIndex: "departamento", key: "departamento", width: 180 },
    { title: "Municipio",    dataIndex: "municipio",    key: "municipio",    width: 180 },
    {
      title: "Total Entregas", dataIndex: "totalEntregas", key: "totalEntregas",
      width: 150, align: "center",
      render: (val) => <Tag color="blue">{val.toLocaleString("es-CO")}</Tag>,
    },
  ];

  const labelModo = {
    paginado:    null,
    municipio:   "municipio",
    encuestador: "encuestador",
    documento:   "documento",
    nroEntrega:  "N° entrega",
  }[modoFiltro];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Reporte RIT — Entregas 1</Title>

      {/* ── Acciones principales ──────────────────────────────────────────── */}
      <Space style={{ marginBottom: 16 }}>
        {permissionUpdate && (
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleActualizar}
            disabled={modalVisible || descargando || loadingInicial}
          >
            Actualizar datos
          </Button>
        )}

        <Tooltip
          title={
            descargando
              ? `Descargando... ${filasDescargadas.toLocaleString("es-CO")} de ${totalRegistros.toLocaleString("es-CO")} registros`
              : vistaNacional && modoFiltro === "paginado"
              ? `Exportar todos los registros (${totalRegistros.toLocaleString("es-CO")}) — 2 pestañas`
              : dataFiltrada.length === 0
              ? "No hay datos para exportar"
              : `Exportar ${dataFiltrada.length.toLocaleString("es-CO")} registros — 2 pestañas`
          }
        >
          <Button
            icon={descargando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={descargarExcel}
            loading={descargando}
            disabled={descargando || loadingInicial || dataFiltrada.length === 0}
            style={{ borderColor: "#217346", color: descargando ? undefined : "#217346" }}
          >
            {descargando ? `Descargando ${progresoDescarga}%` : "Descargar Excel"}
          </Button>
        </Tooltip>

        <Button
          icon={<UserOutlined />}
          type={modoResumen ? "primary" : "default"}
          disabled={loadingInicial || descargando}
          onClick={async () => {
            const nuevoModo = !modoResumen;
            setModoResumen(nuevoModo);
            if (!nuevoModo) {
              setRawDataResumen([]);
            } else if (rawDataResumen.length === 0) {
              await cargarResumenCompleto();
            }
          }}
        >
          {modoResumen ? "Ver detalle" : "Resumen"}
        </Button>
      </Space>

      {/* ── Barra de progreso de descarga ─────────────────────────────────── */}
      {descargando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoDescarga}
            status="active"
            strokeColor={{ "0%": "#217346", "100%": "#52c41a" }}
            format={() => (
              <Text style={{ fontSize: 12 }}>
                {filasDescargadas.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
              </Text>
            )}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel (2 pestañas), no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Modal de sincronización ───────────────────────────────────────── */}
      <Modal
        open={modalVisible}
        footer={sincronizacionFinalizada
          ? <Button type="primary" onClick={cerrarModal}>Cerrar</Button>
          : null}
        closable={false} maskClosable={false} centered width={460}
        title={
          <Space>
            {sincronizacionFinalizada
              ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
              : <SyncOutlined spin style={{ color: "#1677ff", fontSize: 18 }} />}
            <span>
              {sincronizacionFinalizada ? "Sincronización completada" : "Sincronizando datos de entregas"}
            </span>
          </Space>
        }
      >
        <div style={{ padding: "16px 0" }}>
          <Progress
            percent={Math.round(progreso)}
            status={sincronizacionFinalizada ? "success" : "active"}
            strokeColor={sincronizacionFinalizada ? "#52c41a" : { "0%": "#1677ff", "100%": "#36cfc9" }}
          />
          <div style={{ marginTop: 12, minHeight: 44 }}>
            {sincronizacionFinalizada ? (
              <Space direction="vertical" size={2}>
                <Text strong style={{ color: "#52c41a" }}>{mensajeActual}</Text>
                <Text type="secondary">
                  Se sincronizaron{" "}
                  <Text strong>{totalSincronizado.toLocaleString("es-CO")}</Text>{" "}
                  registros. La tabla ha sido actualizada.
                </Text>
              </Space>
            ) : (
              <Text type="secondary">{mensajeActual}</Text>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Cuerpo principal ──────────────────────────────────────────────── */}
      {loadingInicial ? (
        <div style={{
          display:        "flex",
          flexDirection:  "column",
          justifyContent: "center",
          alignItems:     "center",
          minHeight:      380,
          gap:            20,
        }}>
          <Spin
            size="large"
            indicator={<SyncOutlined spin style={{ fontSize: 52, color: "#1677ff" }} />}
          />
          <div style={{ textAlign: "center" }}>
            <Text strong style={{ fontSize: 16, color: "#1677ff", display: "block", marginBottom: 4 }}>
              Cargando reporte de entregas
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {mensajeCarga}
            </Text>
          </div>
          <div style={{ width: 340 }}>
            <Progress
              percent={100}
              status="active"
              showInfo={false}
              strokeColor={{ "0%": "#1677ff", "100%": "#36cfc9" }}
              size="small"
            />
          </div>
        </div>
      ) : (
        <Spin spinning={loadingConsultar} tip="Cargando datos..." size="large">
          <div style={{ minHeight: loadingConsultar ? 400 : "auto" }}>

            
              
                <Divider orientation="left" style={{ margin: "12px 0" }}>
                  Filtros de tabla
                </Divider>

                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  {/* Selector de municipio: solo visible para admin */}
                  {vistaNacional && (
                    <Col xs={24} sm={12} md={6}>
                      <Select
                        placeholder="Filtrar por municipio..."
                        style={{ width: "100%" }}
                        value={municipioAdmin}
                        onChange={handleMunicipioAdminChange}
                        loading={loadingMunicipios}
                        allowClear showSearch optionFilterProp="label"
                        options={municipios.map((m) => ({ value: m.municipio_id, label: m.municipio }))}
                        suffixIcon={<FilterOutlined />}
                        disabled={descargando}
                      />
                    </Col>
                  )}

                  <Col xs={24} sm={12} md={6}>
                    <Input
                      placeholder="Buscar por N° documento"
                      prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                      value={filtroDocumento}
                      onChange={handleDocumentoChange}
                      allowClear
                    />
                  </Col>

                  {!esCoordinador && (
                    <Col xs={24} sm={12} md={6}>
                      <Select
                        placeholder="Filtrar por encuestador"
                        style={{ width: "100%" }}
                        value={filtroEncuestador}
                        onChange={handleEncuestadorChange}
                        loading={loadingEncuestadores}
                        allowClear showSearch optionFilterProp="label"
                        options={encuestadoresEfectivos}
                      />
                    </Col>
                  )}

                  <Col xs={24} sm={8} md={4}>
                    <Select
                      placeholder="N° Entrega"
                      style={{ width: "100%" }}
                      value={filtroNroEntrega}
                      onChange={handleNroEntregaChange}
                      allowClear
                    >
                      {entregasDisponibles.map((n) => (
                        <Option key={n} value={n}>Entrega {n}</Option>
                      ))}
                    </Select>
                  </Col>

                  <Col xs={24} sm={8} md={4}>
                    <Select
                      placeholder="Estado"
                      style={{ width: "100%" }}
                      value={filtroEstado}
                      onChange={handleEstadoChange}
                      allowClear
                    >
                      <Option value={1}>Activo</Option>
                      <Option value={0}>Inactivo</Option>
                    </Select>
                  </Col>

                  {hayFiltrosActivos && (
                    <Col>
                      <Button icon={<ClearOutlined />} onClick={limpiarFiltros}>
                        Limpiar
                      </Button>
                    </Col>
                  )}
                </Row>

                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Col>
                    <Text type="secondary">
                      {modoFiltro === "paginado" ? (
                        <>
                          Mostrando{" "}
                          <Text strong>
                            {cursorIndex * pageSize + 1}–{Math.min((cursorIndex + 1) * pageSize, totalRegistros)}
                          </Text>{" "}
                          de <Text strong>{totalRegistros.toLocaleString("es-CO")}</Text> registros
                          {" "}· página <Text strong>{cursorIndex + 1}</Text>
                          {hayFiltrosActivos && <Text type="warning"> (con filtros aplicados)</Text>}
                        </>
                      ) : (
                        <>
                          Mostrando{" "}
                          <Text strong>
                            {modoResumen
                              ? dataFiltradaResumen.length.toLocaleString("es-CO")
                              : dataFiltrada.length.toLocaleString("es-CO")}
                          </Text>
                          {hayFiltrosActivos && (
                            <Text type="secondary">
                              {" "}de <Text strong>{rawData.length.toLocaleString("es-CO")}</Text>
                            </Text>
                          )}{" "}
                          registro{dataFiltrada.length !== 1 ? "s" : ""}
                          {labelModo && (
                            <Text type="secondary">
                              {" "}· filtrado por <Text strong>{labelModo}</Text>
                            </Text>
                          )}
                          {hayFiltrosActivos && <Text type="warning"> (filtros aplicados)</Text>}
                        </>
                      )}
                    </Text>
                  </Col>

                  {/* Controles de paginación: solo para admin en modo paginado */}
                  {vistaNacional && modoFiltro === "paginado" && (
                    <Col>
                      <Space>
                        <Space size={4}>
                          <Text type="secondary" style={{ fontSize: 13 }}>Filas:</Text>
                          <Select
                            size="small"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={{ width: 80 }}
                            disabled={descargando}
                            options={PAGE_SIZE_OPTIONS.map((s) => ({ value: s, label: s }))}
                          />
                        </Space>
                        <Button
                          size="small" icon={<LeftOutlined />}
                          onClick={irPaginaAnterior}
                          disabled={cursorIndex === 0 || loadingConsultar || descargando}
                        >
                          Anterior
                        </Button>
                        <Button
                          size="small" icon={<RightOutlined />} iconPosition="end"
                          onClick={irSiguientePagina}
                          disabled={!hasMore || loadingConsultar || descargando}
                        >
                          Siguiente
                        </Button>
                      </Space>
                    </Col>
                  )}
                </Row>
                {modoResumen ? (
                  <>
                    {loadingResumen && (
                      <div style={{ marginBottom: 16, maxWidth: 500 }}>
                        {/* deja exactamente el mismo Progress que ya tienes */}
                      </div>
                    )}

                    <Table
                      columns={columnsResumen}
                      dataSource={loadingResumen ? [] : resumenData}
                      rowKey="key"
                      size="middle"
                      bordered
                      scroll={{ x: 600 }}
                      pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        pageSizeOptions: ["30", "50", "100"],
                        showTotal: (total) =>
                          `${total} municipio${total !== 1 ? "s" : ""} · ${dataFiltradaResumen.length.toLocaleString("es-CO")} registros`,
                      }}
                    />
                  </>
                ) : dataFiltrada.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No se encontraron registros de entregas"
                    style={{ marginTop: 40, marginBottom: 40 }}
                  />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={dataFiltrada}
                    rowKey="key"
                    scroll={{ x: 2200 }}
                    size="middle"
                    bordered
                    pagination={
                      modoFiltro === "paginado"
                        ? false
                        : {
                            pageSize: 50,
                            showSizeChanger: true,
                            pageSizeOptions: ["30", "50", "100"],
                            showTotal: (total, range) =>
                              `${range[0]}–${range[1]} de ${total} registros`,
                          }
                    }
                  />
                )}
            
          </div>
        </Spin>
      )}
    </Card>
  );
};

export default ReporteEntregas;
